import React from 'react';
import { Shield, User, Database, Lock, Mail, Globe, FileText } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[var(--bg-base)] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4 flex items-center gap-3">
            <Shield className="w-8 h-8 text-violet-600" />
            Privacy Policy
          </h1>
          <p className="text-stone-500">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="prose max-w-none space-y-8">
          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Introduction</h2>
            <p className="text-stone-600">
              Line Dance Central ("we", "us", or "our") is committed to protecting your privacy. This 
              Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
              you visit our website linedancecentral.uk and use our services.
            </p>
            <p className="text-stone-600 mt-4">
              We are registered in England and Wales and comply with the UK General Data Protection 
              Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-violet-600" />
              Information We Collect
            </h2>
            
            <h3 className="text-lg font-medium text-stone-900 mt-4 mb-2">Personal Information</h3>
            <p className="text-stone-600 mb-2">When you use our services, we may collect:</p>
            <ul className="list-disc list-inside text-stone-600 space-y-1">
              <li>Name and contact details (email address, phone number)</li>
              <li>Account credentials (email and password)</li>
              <li>Booking information (event details, guest names, dietary requirements)</li>
              <li>Payment information (processed securely by Square - we do not store card details)</li>
              <li>Communications you send to us</li>
            </ul>

            <h3 className="text-lg font-medium text-stone-900 mt-6 mb-2">Automatically Collected Information</h3>
            <p className="text-stone-600 mb-2">When you visit our website, we automatically collect:</p>
            <ul className="list-disc list-inside text-stone-600 space-y-1">
              <li>IP address and general location</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Pages visited and time spent</li>
              <li>Referring website</li>
            </ul>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-violet-600" />
              How We Use Your Information
            </h2>
            <p className="text-stone-600 mb-4">We use your information to:</p>
            <ul className="list-disc list-inside text-stone-600 space-y-2">
              <li>Provide and manage your account</li>
              <li>Process bookings and payments</li>
              <li>Send booking confirmations and important updates</li>
              <li>Respond to your enquiries and provide customer support</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
              <li>Prevent fraud and maintain security</li>
            </ul>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Legal Basis for Processing</h2>
            <p className="text-stone-600 mb-4">We process your personal data based on:</p>
            <ul className="list-disc list-inside text-stone-600 space-y-2">
              <li><strong>Contract:</strong> To fulfil our contract with you (e.g., processing bookings)</li>
              <li><strong>Consent:</strong> Where you have given consent (e.g., marketing communications)</li>
              <li><strong>Legitimate interests:</strong> For our legitimate business interests (e.g., improving services)</li>
              <li><strong>Legal obligation:</strong> To comply with applicable laws</li>
            </ul>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-violet-600" />
              Sharing Your Information
            </h2>
            <p className="text-stone-600 mb-4">We may share your information with:</p>
            <ul className="list-disc list-inside text-stone-600 space-y-2">
              <li><strong>Event organisers:</strong> To facilitate your bookings</li>
              <li><strong>Payment processors:</strong> Square processes payments securely</li>
              <li><strong>Service providers:</strong> Who help us operate our website (e.g., hosting, email)</li>
              <li><strong>Legal authorities:</strong> When required by law</li>
            </ul>
            <p className="text-stone-600 mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-violet-600" />
              Data Security
            </h2>
            <p className="text-stone-600">
              We implement appropriate technical and organisational measures to protect your personal 
              data against unauthorised access, alteration, disclosure, or destruction. This includes:
            </p>
            <ul className="list-disc list-inside text-stone-600 space-y-2 mt-4">
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Secure password hashing</li>
              <li>Regular security assessments</li>
              <li>Limited access to personal data on a need-to-know basis</li>
            </ul>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Data Retention</h2>
            <p className="text-stone-600">
              We retain your personal data only for as long as necessary to fulfil the purposes for 
              which it was collected, including to satisfy legal, accounting, or reporting requirements.
            </p>
            <ul className="list-disc list-inside text-stone-600 space-y-2 mt-4">
              <li>Account data: Until you delete your account</li>
              <li>Booking records: 7 years (for tax and legal purposes)</li>
              <li>Marketing preferences: Until you withdraw consent</li>
            </ul>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-600" />
              Your Rights
            </h2>
            <p className="text-stone-600 mb-4">Under UK GDPR, you have the right to:</p>
            <ul className="list-disc list-inside text-stone-600 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Restrict processing:</strong> Request limitation of processing</li>
              <li><strong>Data portability:</strong> Request transfer of your data</li>
              <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Withdraw consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p className="text-stone-600 mt-4">
              To exercise these rights, please contact us using the details below. We will respond 
              within one month.
            </p>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Cookies</h2>
            <p className="text-stone-600">
              Our website uses cookies to enhance your experience. For detailed information about the 
              cookies we use and how to manage them, please see our{' '}
              <a href="/cookies" className="text-violet-600 hover:text-violet-700">Cookie Policy</a>.
            </p>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Changes to This Policy</h2>
            <p className="text-stone-600">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date. 
              We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-violet-600" />
              Contact Us
            </h2>
            <p className="text-stone-600 mb-4">
              If you have any questions about this Privacy Policy or wish to exercise your rights, 
              please contact us:
            </p>
            <div className="bg-stone-50 rounded p-4">
              <p className="text-stone-600">
                <strong>Email:</strong>{' '}
                <a href="mailto:hi@linedancecentral.uk" className="text-violet-600 hover:text-violet-700">
                  hi@linedancecentral.uk
                </a>
              </p>
              <p className="text-stone-600 mt-2">
                <strong>Website:</strong>{' '}
                <a href="https://linedancecentral.uk" className="text-violet-600 hover:text-violet-700">
                  linedancecentral.uk
                </a>
              </p>
            </div>
            <p className="text-stone-600 mt-4">
              You also have the right to lodge a complaint with the Information Commissioner's Office (ICO), 
              the UK supervisory authority for data protection issues:{' '}
              <a href="https://ico.org.uk" className="text-violet-600 hover:text-violet-700" target="_blank" rel="noopener noreferrer">
                ico.org.uk
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
